import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridEventListener } from '@mui/x-data-grid';
import { extractFiltersFromModel, resolveProvinceSearchMatches } from '../../../utils/gridFilterUtils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { schoolApi } from '../../../services';
import { useErrorHandler } from '../../../lib/errorHandler';
import { useAppSelector } from '../../../store/hooks';
import { SchoolYearStatsSchema } from '../../../services/schoolApi';
import { z } from 'zod';
import { MOBILE_BREAKPOINT, ROW_HEIGHTS, LIST_HEIGHTS } from '../../../utils/responsive';
import { useEntityListState, createEntityToolbar } from '../../../hooks';

type SchoolYearStats = z.infer<typeof SchoolYearStatsSchema>;
import { BaseDataGrid, PageHeader, DeleteConfirmDialog, SchoolDialog, PersistentTabs, TabConfig } from '../../../components';
import { THPTChart } from './THPTChart';

const THPTToolbar = createEntityToolbar({
	searchPlaceholder: 'Tìm trường hoặc tỉnh/thành...',
	exportFn: (params) => schoolApi.exportToExcel(params),
});
import { useTHPTColumns, useTHPTQuery, buildTHPTExportParams, type UseTHPTQueryParams } from '../hooks';

interface THPTContentProps {
	isAdmin: boolean;
	canEdit?: boolean;
}



export default function THPTContent({ isAdmin, canEdit = false }: THPTContentProps) {
	const { showError, showSuccess } = useErrorHandler();
	const queryClient = useQueryClient();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);
	const mergedProvinces = useAppSelector((state) => state.filters.mergedProvinces);
	const provinces = useAppSelector((state) => state.filters.provinces);

	const {
		searchValue, paginationModel, sortModel, filterModel,
		handlePaginationModelChange, handleSortModelChange,
		handleFilterModelChange, handleSearch,
	} = useEntityListState({ defaultSortField: 'tenTruong' });

	const [schoolDialog, setSchoolDialog] = useState<{
		open: boolean;
		mode: 'create' | 'edit' | 'view';
		school: SchoolYearStats | null;
	}>({ open: false, mode: 'create', school: null });
	const dialogRef = useRef(schoolDialog);
	dialogRef.current = schoolDialog;

	const [deleteDialog, setDeleteDialog] = useState<{
		open: boolean;
		school: SchoolYearStats | null;
	}>({ open: false, school: null });

	const extractedFilters = useMemo(() =>
		extractFiltersFromModel(filterModel, mergedProvinces, provinces),
		[filterModel, mergedProvinces, provinces]
	);

	const hasSearchValue = searchValue.trim().length > 0;
	// Ô tìm kiếm phải hiểu cả tên tỉnh gốc lẫn tên tỉnh sau sáp nhập: cột "Sau
	// sáp nhập" trên lưới hiển thị tên từ danh mục mới, nên gõ đúng tên đang
	// thấy trên màn hình phải ra kết quả.
	const provinceSearchMatches = useMemo(
		() => resolveProvinceSearchMatches(searchValue, provinces, mergedProvinces),
		[searchValue, provinces, mergedProvinces]
	);
	const hasProvinceNameMatch = provinceSearchMatches.maTinh.length > 0 || provinceSearchMatches.maTinhMoi.length > 0;

	const resolvedMaTinh = extractedFilters.maTinh ?? (provinceSearchMatches.maTinh.length > 0 ? provinceSearchMatches.maTinh : undefined);
	const resolvedMaTinhMoi = extractedFilters.maTinhMoi ?? (provinceSearchMatches.maTinhMoi.length > 0 ? provinceSearchMatches.maTinhMoi : undefined);
	const resolvedQuery = hasProvinceNameMatch ? undefined : (hasSearchValue ? searchValue : undefined);
	// Chỉ OR khi CẢ HAI mã đến từ chính ô tìm kiếm tự do (không có bộ lọc cột
	// nào đang chủ động) — bộ lọc theo cột (tenTinh/tenTinhSauSapNhap) vẫn AND.
	const provinceQueryOr = hasProvinceNameMatch && !extractedFilters.maTinh && !extractedFilters.maTinhMoi;

	const queryParams: UseTHPTQueryParams = {
		filters: {
			query: resolvedQuery,
			maKhoa: selectedFaculty ? [selectedFaculty] : undefined,
			maTinh: resolvedMaTinh
		},
		page: paginationModel.page,
		pageSize: paginationModel.pageSize,
		sortBy: sortModel.length > 0 ? sortModel[0].field : undefined,
		sortOrder: (sortModel.length > 0 ? sortModel[0].sort : undefined) as 'asc' | 'desc' | undefined,
		maTinhMoi: resolvedMaTinhMoi,
		hasCodeNameChange: extractedFilters.hasCodeNameChange,
		top149: extractedFilters.top149,
		provinceQueryOr,
	};
	const { data: schoolsData, isLoading, refetch } = useTHPTQuery(queryParams);
	const exportParams = buildTHPTExportParams(queryParams);

	const stats = useMemo(() => schoolsData?.data?.stats || [], [schoolsData?.data?.stats]);
	const totalCount = schoolsData?.data?.total || 0;

	// Sync URL params with dialog state
	useEffect(() => {
		const action = searchParams.get('action') as 'create' | 'edit' | 'view' | null;
		const schoolId = searchParams.get('id');

		// Security: Redirect unauthorized users from edit/create to view mode
		if (action && ['create', 'edit'].includes(action) && !canEdit) {
			if (schoolId) {
				setSearchParams({ action: 'view', id: schoolId });
			} else {
				setSearchParams({});
			}
			return;
		}

		if (action && ['create', 'edit', 'view'].includes(action)) {
			if (action === 'create') {
				if (!dialogRef.current.open || dialogRef.current.mode !== 'create') {
					setSchoolDialog({
						open: true,
						mode: 'create',
						school: null,
					});
				}
			} else if (schoolId && stats.length > 0) {
				if (dialogRef.current.open
					&& dialogRef.current.school?.maTruong === schoolId
					&& dialogRef.current.mode === action) {
					return;
				}
				const school = stats.find((s) => s.maTruong === schoolId);
				if (school) {
					setSchoolDialog({
						open: true,
						mode: action,
						school,
					});
				}
			}
		} else if (!action && dialogRef.current.open) {
			setSchoolDialog({ open: false, mode: 'create', school: null });
		}
	}, [searchParams, stats, canEdit, setSearchParams]);

	const deleteMutation = useMutation({
		mutationFn: schoolApi.deleteSchool,
		onSuccess: () => {
			showSuccess('Xóa trường THPT thành công');
			queryClient.invalidateQueries({ queryKey: ['thpt'] });
			setDeleteDialog({ open: false, school: null });
		},
		onError: (error: Error) => {
			showError(error.message || 'Có lỗi xảy ra khi xóa trường THPT');
		},
	});

	const handleRefresh = useCallback(() => {
		if (isLoading) return;
		refetch();
	}, [isLoading, refetch]);

	const handleCreate = useCallback(() => {
		setSearchParams({ action: 'create' });
	}, [setSearchParams]);

	const handleEdit = useCallback(
		(school: SchoolYearStats) => () => {
			setSearchParams({ action: 'edit', id: school.maTruong });
		},
		[setSearchParams],
	);

	const handleDelete = useCallback(
		(school: SchoolYearStats) => () => {
			setDeleteDialog({
				open: true,
				school,
			});
		},
		[],
	);

	const handleDeleteConfirm = useCallback(() => {
		if (deleteMutation.isPending || !deleteDialog.school?.maTruong) return;

		deleteMutation.mutate(deleteDialog.school.maTruong);
	}, [deleteDialog.school, deleteMutation]);

	const handleCloseSchoolDialog = useCallback(() => {
		setSearchParams({});
	}, [setSearchParams]);

	const handleCloseDeleteDialog = useCallback(() => {
		setDeleteDialog({ open: false, school: null });
	}, []);

	const handleRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
		setSearchParams({ action: canEdit ? 'edit' : 'view', id: (params.row as SchoolYearStats).maTruong });
	}, [canEdit, setSearchParams]);

	const columns = useTHPTColumns({
		isAdmin,
		isMobile,
		onEdit: handleEdit,
		onDelete: handleDelete
	});

	const tabs: TabConfig[] = [
		{
			label: 'Danh sách',
			content: (
				<Box sx={{
					flex: 1,
					minHeight: 0,
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					display: 'flex',
					flexDirection: 'column'
				}}>
					<BaseDataGrid
						rows={stats}
						columns={columns}
						loading={isLoading}
						onRowClick={handleRowClick}
						getRowId={(row) => row.maTruong}
						rowCount={totalCount}
						paginationModel={paginationModel}
						onPaginationModelChange={handlePaginationModelChange}
						sortModel={sortModel}
						onSortModelChange={handleSortModelChange}
						filterModel={filterModel}
						onFilterModelChange={handleFilterModelChange}
						pageSizeOptions={[10, 25, 50, 100]}
						paginationMode="server"
						sortingMode="server"
						filterMode="server"
						disableRowSelectionOnClick
						rowHeight={isMobile ? undefined : ROW_HEIGHTS.DESKTOP}
						getRowHeight={isMobile ? () => 'auto' : undefined}
						slots={{
							toolbar: () => (
								<THPTToolbar
									searchValue={searchValue}
									onSearch={handleSearch}
									onRefresh={handleRefresh}
									exportParams={exportParams}
									isMobile={isMobile}
								/>
							),
						}}
					/>
				</Box>
			)
		},
		{
			label: 'Biểu đồ thống kê',
			content: (
				<Box sx={{
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					overflow: 'auto'
				}}>
					<THPTChart height={500} />
				</Box>
			)
		}
	];

	return (
		<Box sx={{
			width: '100%',
			height: { xs: '100%', md: '100vh' },
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',
			p: 0
		}}>
			<PageHeader
				title="Danh sách Trường THPT"
				onRefresh={handleRefresh}
				onAdd={canEdit ? handleCreate : undefined}
				isAdmin={isAdmin}
				loading={isLoading}
			/>

			<PersistentTabs
				pageKey="thpt"
				tabs={tabs}
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					minHeight: 0
				}}
			/>

			<DeleteConfirmDialog
				open={deleteDialog.open}
				onClose={handleCloseDeleteDialog}
				onConfirm={handleDeleteConfirm}
				title="Xóa trường THPT"
				message={`Bạn có chắc chắn muốn xóa trường "${deleteDialog.school?.tenTruong}"? Hành động này không thể hoàn tác.`}
				isLoading={deleteMutation.isPending}
			/>

			<SchoolDialog
				open={schoolDialog.open}
				onClose={handleCloseSchoolDialog}
				school={schoolDialog.school}
				mode={schoolDialog.mode}
				disabled={!canEdit}
			/>
		</Box>
	);
}